import {useAuth0} from '@auth0/auth0-react';
import {SWR_OPTIONS} from 'appConstants';
import axios from 'axios';
import {Loading} from 'components/Loading';
import {FilterContextProvider} from 'context/FilterContext';
import {InputMenuProvider} from 'context/InputMenu';
import {OnboardingContextProvider} from 'context/OnboaringContext';
import SettingsProvider from 'context/SettingsProvider';
import {SnackbarUtilsConfigurator} from 'helpers/Alert';
import {SnackbarProvider} from 'notistack';
import {Suspense, useEffect} from 'react';
import {Outlet} from 'react-router-dom';
import {IntercomProvider} from 'react-use-intercom';
import {onRequest} from 'services/interceptorHandler';
import {SWRConfig} from 'swr';
import {withOAuth0} from 'withOAuth0';
import {getIntercomAppId} from './utils/runtimeVars';
import LaunchDarkly from './integrations/launchdarkly';
import Hotjar from './integrations/hotjar';
import * as Sentry from '@sentry/react';

const INTERCOM_APP_ID = getIntercomAppId();

function App() {
  const {getAccessTokenSilently, logout, isAuthenticated, user} = useAuth0();

  const hostname = window.location.hostname?.split('.');
  const titleAppend =
    hostname.length === 4 ? `${capitalizeFirstLetter(hostname[1])}` : hostname.length === 1 ? hostname[0] : '';

  useEffect(() => {
    document.title = `Bioplan ${titleAppend.toUpperCase()}`;
    axios.interceptors.request.use(async (config) => await onRequest({config, getAccessTokenSilently, logout}));
    if (isAuthenticated && user) {
      Sentry.setUser({
        email: user.email,
        id: user.sub,
        username: user.nickname,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [isAuthenticated, user]);

  return (
    <Suspense fallback={<Loading />}>
      <IntercomProvider appId={INTERCOM_APP_ID}>
        <LaunchDarkly.Component />
        <Hotjar />
        <SWRConfig value={SWR_OPTIONS}>
          <SettingsProvider>
            <OnboardingContextProvider>
              <InputMenuProvider>
                <FilterContextProvider>
                  <SnackbarProvider maxSnack={3}>
                    <SnackbarUtilsConfigurator />
                    <Outlet />
                  </SnackbarProvider>
                </FilterContextProvider>
              </InputMenuProvider>
            </OnboardingContextProvider>
          </SettingsProvider>
        </SWRConfig>
      </IntercomProvider>
    </Suspense>
  );
}

export const Application = withOAuth0(App);

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
